import { Injectable } from '@angular/core';
import { addHandler, updatedChannelEvent } from '../models/events';
import { ColorDeciderService, DefaultColorName } from './color-decider.service';
import * as echarts from 'echarts';
import { ECharts, EChartsOption } from 'echarts';
import { FlagsService } from './channel-flags.service';
import { FlagOption, getStatusString } from '../models/flags';
import { AlarmLevel } from '../models/channelType';

enum BarScaleType {
    UNDEFINED = 'undefined',
    SINGLE = 'single',
    ZERO = 'zero',
}

@Injectable({ providedIn: 'root' })
export class ControlElementService {
    static readonly XHTML = 'http://www.w3.org/1999/xhtml';
    static readonly DESIGN_WIDTH = 111;
    static readonly GAP = 4;
    static readonly LABELS_W = 27.5;
    static readonly METER_W = 24;
    static readonly FONT_SIZE = 8;
    static readonly TICK_COUNT = 7;
    private GRAPH_UNDEFINED_COLOR = '#9cb2fb';
    private FLAG_UNDEFINED_COLOR = '#20e420';

    constructor(
        private colorDeciderService: ColorDeciderService,
        private flagsService: FlagsService
    ) {}

    udpateInputUnit(foreignObject, units) {
        const unitsDivision = foreignObject.querySelector('[unitsDivision="true"]') as HTMLElement;
        unitsDivision.innerText = units;
    }

    updateBarDisplay(foreignObject, channel, systemColors) {
        const rangesContainer = foreignObject.querySelector('[rangesContainer="true"]') as HTMLElement;
        const valueContainer = foreignObject.querySelector('[valueContainer="true"]') as HTMLElement;
        const channelNameContainer = foreignObject.querySelector('[channelNameContainer="true"]') as HTMLElement;
        const linearGaugeRoot = foreignObject.querySelector('[linearGaugeRoot="true"]') as HTMLElement;
        const indicatorPointer = foreignObject.querySelector('[indicatorPointer="true"]') as HTMLElement;

        const scaleType = linearGaugeRoot.getAttribute('bar-scale-type');
        const intervalType = Number(linearGaugeRoot.getAttribute('bar-intervals'));

        let barSetting;
        if (scaleType || intervalType) {
            barSetting = {
                interval: intervalType,
                scale: scaleType,
            };
        }

        if (channel) {
            channelNameContainer.textContent = channel.opsData.name;
            valueContainer.textContent = `- (${channel.opsData.measurementUnit})`;
        } else {
            channelNameContainer.textContent = 'Channel';
            valueContainer.textContent = `- (Units)`;
        }

        if (barSetting) {
            linearGaugeRoot.setAttribute('bar-scale-type', barSetting?.scale);
            linearGaugeRoot.setAttribute('bar-intervals', barSetting?.interval.toString());
        }

        this.applyRanges(rangesContainer, channel, systemColors);
        this.applyTicks(foreignObject, channel, barSetting);
        this.styleLabelsStyle(foreignObject);
    }

    private scaleValue(channel, value: number) {
        return value / channel.fcuData.scale;
    }

    private applyStyle(el: HTMLElement, style: Partial<CSSStyleDeclaration>): HTMLElement {
        Object.assign(el.style, style);
        return el;
    }

    private div(name: string, style: Partial<CSSStyleDeclaration>): HTMLDivElement {
        const el = document.createElement('div');
        el.setAttribute('xmlns', ControlElementService.XHTML);
        el.setAttribute('data-part', name);
        this.applyStyle(el, { boxSizing: 'border-box', ...style });
        return el;
    }

    private styleLabelsStyle(foreignObject) {
        const width = parseFloat(foreignObject.getAttribute('width') ?? '0');
        const scale = width / ControlElementService.DESIGN_WIDTH;
        const labelsContainer = foreignObject.querySelector('[labelsContainer="true"]') as HTMLElement;
        labelsContainer.style.flex = '0 0 auto';
        labelsContainer.style.flexDirection = 'column';
        labelsContainer.style.justifyContent = 'space-between';
        labelsContainer.style.alignItems = 'flex-end';
        labelsContainer.style.width = `${ControlElementService.LABELS_W * scale}px`;
        labelsContainer.style.padding = '0';

        const fontSize = `${ControlElementService.FONT_SIZE * scale}px`;
        labelsContainer.querySelectorAll<HTMLElement>('[labelValue="true"]').forEach(label => {
            label.style.display = 'flex';
            label.style.alignItems = 'center';
            label.style.justifyContent = 'flex-end';
            label.style.textAlign = 'right';
            label.style.whiteSpace = 'nowrap';
            label.style.flex = 'none';
            label.style.fontFamily = "'Noto Sans', sans-serif";
            label.style.fontStyle = 'normal';
            label.style.fontWeight = '500';
            label.style.fontSize = fontSize;
            label.style.lineHeight = `${7 * scale}px`;
            label.style.color = 'rgba(255, 255, 255, 0.7)';
        });
    }

    private calculateRanges(channel, rangeMin: number, rangeMax: number) {
        const fcuAlarms = channel.fcuData;
        const rangeOffset = 1 / channel.fcuData.scale;
        const rangeMap = new Map();

        const toRange = (min: number, max: number, zeroOut: boolean = false): { min: number; max: number } => {
            if (max - min == 0 && !zeroOut) max += rangeOffset;
            return zeroOut ? { min: 0, max: 0 } : { min, max };
        };

        const gapRange = (min: number, max: number): { min: number; max: number } => {
            if (max - min <= rangeOffset) return { min: 0, max: 0 };
            if (max != rangeMax) max -= rangeOffset;
            return { min, max };
        };

        const veryLow = toRange(
            rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryLow.setPoint),
            !fcuAlarms.alarmSettingVeryLow.enable
        );
        const low = toRange(
            fcuAlarms.alarmSettingVeryLow.enable ? veryLow.max : rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingLow.setPoint),
            !fcuAlarms.alarmSettingLow.enable
        );
        const veryHigh = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryHigh.setPoint),
            rangeMax,
            !fcuAlarms.alarmSettingVeryHigh.enable
        );
        const high = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingHigh.setPoint),
            fcuAlarms.alarmSettingVeryHigh.enable ? veryHigh.min : rangeMax,
            !fcuAlarms.alarmSettingHigh.enable
        );

        const hasNormalRange = fcuAlarms.normalRange.min !== 0 || fcuAlarms.normalRange.max !== 0;
        const normal = toRange(
            this.scaleValue(channel, fcuAlarms.normalRange.min),
            this.scaleValue(channel, fcuAlarms.normalRange.max),
            !hasNormalRange
        );
        const withoutNormalRange = ((high.min || rangeMax) - (low.max || rangeMin)) / 2;

        const lowToNormalGap = gapRange(
            low.max || rangeMin,
            hasNormalRange ? normal.min : (low.max || rangeMin) + withoutNormalRange
        );

        const normalToHighGap = gapRange(hasNormalRange ? normal.max : lowToNormalGap.max, high.min || rangeMax);

        rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);
        rangeMap.set(FlagOption.HIGH, high);
        rangeMap.set('normalToHigh', normalToHighGap);
        rangeMap.set(FlagOption.NORMAL, normal);
        rangeMap.set('lowToNormal', lowToNormalGap);
        rangeMap.set(FlagOption.LOW, low);
        rangeMap.set(FlagOption.LOW_LOW, veryLow);

        return rangeMap;
    }

    private getAlarmStateColor(alarmState, channel, alarmColors) {
        const alarmLevelMap = {
            [AlarmLevel.Caution]: 'Caution',
            [AlarmLevel.Warning]: 'Warning',
            [AlarmLevel.Alarm]: 'Alarm',
            [AlarmLevel.None]: 'None',
        };
        if (alarmState === FlagOption.LOW) {
            return alarmColors[alarmLevelMap[channel.fcuData.alarmSettingLow.alarmLevel]];
        } else if (alarmState === FlagOption.LOW_LOW) {
            return alarmColors[alarmLevelMap[channel.fcuData.alarmSettingVeryLow.alarmLevel]];
        } else if (alarmState === FlagOption.HIGH) {
            return alarmColors[alarmLevelMap[channel.fcuData.alarmSettingHigh.alarmLevel]];
        } else if (alarmState === FlagOption.HIGH_HIGH) {
            return alarmColors[alarmLevelMap[channel.fcuData.alarmSettingVeryHigh.alarmLevel]];
        } else if (alarmState === FlagOption.DIGITAL) {
            return alarmColors[alarmLevelMap[channel.fcuData.digitalAlarmSetting.alarmLevel]];
        } else if (alarmState === FlagOption.UNDEFINED) {
            return this.GRAPH_UNDEFINED_COLOR;
        } else {
            return alarmColors[alarmState] || this.GRAPH_UNDEFINED_COLOR;
        }
    }

    private applyRanges(rangesContainer, channel, systemColors?) {
        if (!channel) {
            rangesContainer.replaceChildren();
            const rangeTop = this.div('range', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                padding: '1px 1px 0.5px',
                width: '100%',
                flex: '1 1 0',
            });
            rangeTop.appendChild(
                this.div('rangeFill', {
                    width: '2px',
                    alignSelf: 'stretch',
                    backgroundColor: 'rgba(156, 178, 251, 0.7)',
                    borderRadius: '2px 2px 0px 0px',
                    flex: 'none',
                })
            );

            const rangeBottom = this.div('range', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0.5px 1px 1px',
                width: '100%',
                flex: '1 1 0',
            });
            rangeBottom.appendChild(
                this.div('rangeFill', {
                    width: '2px',
                    alignSelf: 'stretch',
                    backgroundColor: 'rgba(255, 59, 48, 0.7)',
                    borderRadius: '0px 0px 2px 2px',
                    flex: 'none',
                })
            );
            rangesContainer.append(rangeTop, rangeBottom);
        } else {
            const measurableRange = channel.fcuData.measurableRange;
            const rangeMax = this.scaleValue(channel, measurableRange.max);
            const rangeMin = this.scaleValue(channel, measurableRange.min);
            const totalRange = Math.abs(rangeMax - rangeMin);
            const ranges = this.calculateRanges(channel, rangeMin, rangeMax);
            const alarmColors = {
                [DefaultColorName.ALARM]: systemColors[DefaultColorName.ALARM]?.color,
                [DefaultColorName.CAUTION]: systemColors[DefaultColorName.CAUTION]?.color,
                [DefaultColorName.WARNING]: systemColors[DefaultColorName.WARNING]?.color,
                [FlagOption.NORMAL]: this.FLAG_UNDEFINED_COLOR,
            };

            const colorArr: [number, string][] = [];
            let previewRange = 0;
            let rangeKeys = Array.from(ranges.keys());

            for (let i = 0; i < rangeKeys.length; i++) {
                let range = ranges.get(rangeKeys[i]);
                const width = range.max - range.min;
                if (width > 0) {
                    let percentage = previewRange + (range.max - range.min) / totalRange;
                    let color = this.getAlarmStateColor(rangeKeys[i], channel, alarmColors);
                    if (color == this.GRAPH_UNDEFINED_COLOR) color = null;
                    if (rangeKeys[i] === FlagOption.NORMAL) color = this.FLAG_UNDEFINED_COLOR;
                    colorArr.push([percentage, color]);
                    previewRange = percentage;
                }
            }

            rangesContainer.replaceChildren();

            let prev = 0;
            colorArr.forEach(([cumulative, color], i) => {
                const fraction = cumulative - prev;
                prev = cumulative;
                if (fraction <= 0) return;

                const isFirst = i === 0;
                const isLast = i === colorArr.length - 1;

                const segment = this.div('range', {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    width: '100%',
                    flex: `${fraction} 1 0`,
                    minHeight: '0',
                    padding: '0px 1px',
                    boxSizing: 'border-box',
                });

                segment.appendChild(
                    this.div('rangeFill', {
                        width: '2px',
                        alignSelf: 'stretch',
                        backgroundColor: color ? color : 'unset',
                        borderRadius: `${isLast ? '2px' : '0'} ${isLast ? '2px' : '0'} ${isFirst ? '2px' : '0'} ${isFirst ? '2px' : '0'}`,
                        flex: 'none',
                    })
                );
                rangesContainer.appendChild(segment);
            });
        }
    }

    private applyTicks(foreignObject, channel, barSetting) {
        const ticksContainer = foreignObject.querySelector('[ticksContainer="true"]') as HTMLElement;
        const baseContainer = foreignObject.querySelector('[baseContainer="true"]') as HTMLElement;
        const labelsContainer = foreignObject.querySelector('[labelsContainer="true"]') as HTMLElement;
        const linearGaugeRoot = foreignObject.querySelector('[linearGaugeRoot="true"]') as HTMLElement;

        const scaleType = linearGaugeRoot.getAttribute('bar-scale-type');
        const numOfTicks = Number(linearGaugeRoot.getAttribute('bar-intervals'));
        let numDivision = channel ? 2 : 7;
        if (scaleType == BarScaleType.ZERO) numDivision = numOfTicks * 2 + 2 + 1;
        else if (scaleType == BarScaleType.SINGLE) numDivision = numOfTicks + 2;

        ticksContainer.replaceChildren();
        baseContainer.replaceChildren();

        for (let i = 0; i < numDivision; i++) {
            ticksContainer.appendChild(
                this.div('tick', {
                    width: '6px',
                    height: '1px',
                    backgroundColor: '#4A5C70',
                    borderRadius: '180px 0px 0px 180px',
                    flex: 'none',
                })
            );
        }
        const intervalBorder = '0.5px solid #252D39';
        for (let i = 0; i < numDivision - 1; i++) {
            const isFirst = i === 0;
            const isLast = i === numDivision - 1;
            baseContainer.appendChild(
                this.div('interval', {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: isLast ? 'flex-start' : 'flex-end',
                    padding: isLast ? '0.5px 1px 1px' : '1px 1px 0.5px',
                    gap: '5px',
                    width: '100%',
                    flex: '1 1 0',
                    borderTop: isFirst ? 'none' : intervalBorder,
                    borderBottom: isLast ? 'none' : intervalBorder,
                })
            );
        }
        labelsContainer.replaceChildren();
        const labels = [];
        if (channel) {
            const maxRange = this.scaleValue(channel, channel.fcuData.measurableRange.max);
            const minRange = this.scaleValue(channel, channel.fcuData.measurableRange.min);
            const units = channel.opsData.measurementUnit;
            for (let i = 0; i < numDivision; i++) {
                if (i == 0) labels.push(maxRange);
                else if (i + 1 == numDivision) labels.push(`${minRange} (${units})`);
                else {
                    const totalValue = maxRange - minRange;
                    const value = (totalValue / numDivision) * i + 1;
                    labels.push(value.toFixed(2));
                }
            }
        }

        labels.forEach(text => {
            const label = document.createElement('div');
            label.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            label.setAttribute('labelValue', 'true');
            label.textContent = text;
            labelsContainer.appendChild(label);
        });

        labelsContainer.style.display = channel ? 'flex' : 'none';
    }
}
